<template>
  <!-- Nothing to say, nothing shown. `location` is null whenever the region
       could not be established (see useDataResidency.js), and an app bar that
       states a data location must either be right or be quiet. -->
  <v-menu
    v-if="location"
    v-model="open"
    open-on-hover
    open-on-click
    location="bottom end"
    offset="8"
    :close-on-content-click="false"
    :open-delay="120"
    :close-delay="180"
  >
    <template v-slot:activator="{ props: activatorProps }">
      <v-btn
        v-bind="activatorProps"
        variant="text"
        size="default"
        class="mr-1 residency-btn"
        :aria-label="t('residency.a11y', { location })"
      >
        <svg
          class="residency-icon"
          :viewBox="glyph.viewBox"
          width="16"
          height="16"
          fill="currentColor"
          aria-hidden="true"
          focusable="false"
        >
          <path :d="glyph.path" />
        </svg>
        <!-- The caption is state, not prose, so it belongs on the bar; the
             sentence explaining it belongs in the card below and nowhere else.
             Dropped on a narrow window, where the icon alone still opens the
             card and the bar has better uses for the width. -->
        <span class="residency-label d-none d-sm-inline">{{ location }}</span>
      </v-btn>
    </template>

    <v-card max-width="320" elevation="8" rounded="lg">
      <v-card-text class="px-4 py-3">
        <div class="text-body-2 font-weight-medium mb-1">{{ t('residency.title') }}</div>
        <div class="text-body-2 text-medium-emphasis residency-detail">{{ detail }}</div>
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<script setup>
/**
 * Where this account's patient records live, in the app bar.
 *
 * ── One glyph per answer ────────────────────────────────────────────────────
 * A cloud when we hold the records, a database when they sit in the browser on
 * the pharmacist's own machine. Both are Font Awesome Pro Light — the weight
 * the rest of this bar wears — but inlined as path data rather than written as
 * `fa-light fa-cloud` / `fa-light fa-database`.
 *
 * WHY NOT THE CLASSES. This build takes its icons from a Font Awesome Kit, and
 * that Kit is SUBSET: it carries only the name AND weight pairs someone added
 * to it in the fontawesome.com dashboard. It holds `database` in solid alone
 * and `cloud` in no weight at all, so either class would draw Font Awesome's
 * dotted placeholder — no console error, no failed request, and nothing in the
 * repo to point at, because the cause lives in a web dashboard. That is the
 * wall the portal hit in 2026-08 and answered by vendoring the families it
 * renders (app/scripts/vendor-fontawesome.mjs); two paths here are the small
 * version of the same answer. Should the subset ever grow these two pairs,
 * this can go back to <v-icon> with no change in meaning.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDataResidency } from '../../composables/useDataResidency.js';

const { t, te } = useI18n();
const { isOnPremise, region } = useDataResidency();

const open = ref(false);

/**
 * `cloud` and `database` from Font Awesome Pro 6.7.2 Light, the same release
 * the Kit serves this app. Each keeps its own viewBox — Font Awesome does not
 * draw every icon in one box, and forcing them into a shared one would squash
 * the cloud, which is 640 wide against the database's 448.
 *
 * Font Awesome Pro by @fontawesome — Commercial License, (c) Fonticons, Inc.
 */
const GLYPHS = {
  cloud: {
    viewBox: '0 0 640 512',
    path: 'M429.7 135.7c10.3-4.9 21.9-7.7 34.3-7.7c44.2 0 80 35.8 80 80c0 7.7-1.1 15-3 22c-4.1 14.5 2.5 30 15.8 37.1C587.3 283.2 608 315.2 608 352c0 53-43 96-96 96l-368 0C82.1 448 32 397.9 32 336c0-48.8 31.2-90.3 74.8-105.7c12.3-4.3 20.8-15.7 21.3-28.8C131.5 125 194.6 64 272 64c48.7 0 91.7 24.1 117.8 61.2c9 12.7 25.8 17.2 39.9 10.5zM416 106.8C384.1 61.5 331.5 32 272 32c-94.6 0-171.7 74.6-175.8 168.1C40.2 219.9 0 273.2 0 336c0 79.5 64.5 144 144 144l368 0c70.7 0 128-57.3 128-128c0-49.1-27.7-91.8-68.3-113.2c2.8-9.8 4.3-20.1 4.3-30.8c0-61.9-50.1-112-112-112c-17.2 0-33.5 3.9-48 10.8z',
  },
  database: {
    viewBox: '0 0 448 512',
    path: 'M416 416c0 2.4-1 7.4-8.4 14.9c-7.5 7.6-19.9 15.9-37.8 23.6C334.1 469.8 282.6 480 224 480s-110.1-10.2-145.8-25.5c-17.9-7.7-30.3-16-37.8-23.6C33 423.4 32 418.4 32 416l0-84.5c17.2 8.9 38.1 16.3 61.2 21.9C130.7 362.7 175.8 368 224 368s93.3-5.3 130.8-14.5c23.1-5.7 44-13 61.2-21.9l0 84.5zm0-212.5l0 90.6c-14.5 10.6-37.9 20.6-68.9 28.3C312.5 330.9 270 336 224 336s-88.5-5.1-123.1-13.6c-31-7.6-54.4-17.7-68.9-28.3l0-90.6c17.2 8.9 38.1 16.3 61.2 21.9C130.7 234.7 175.8 240 224 240s93.3-5.3 130.8-14.5c23.1-5.7 44-13 61.2-21.9zm0-37.4c-14.5 10.6-37.9 20.6-68.9 28.3C312.5 202.9 270 208 224 208s-88.5-5.1-123.1-13.6c-31-7.6-54.4-17.7-68.9-28.3L32 96c0-2.4 1-7.4 8.4-14.9c7.5-7.6 19.9-15.9 37.8-23.6C113.9 42.2 165.4 32 224 32s110.1 10.2 145.8 25.5c17.9 7.7 30.3 16 37.8 23.6C415 88.6 416 93.6 416 96l0 70.1zM448 416l0-320C448 43 347.7 0 224 0S0 43 0 96L0 416c0 53 100.3 96 224 96s224-43 224-96z',
  },
};

/**
 * Which of the two the bar wears. `isOnPremise` is baked in at build time by
 * DefinePlugin, so this is settled before the first paint and never has to be
 * reactive — unlike `location`, which waits on the backend.
 */
const glyph = isOnPremise ? GLYPHS.database : GLYPHS.cloud;

/**
 * The short state caption: a place name, or the on-premise wording.
 *
 * The region's translated name when we have one, and the backend's own English
 * label when we do not — a region can go live before its string is translated,
 * and "Montreal, Canada" in a Dutch app bar beats the raw key that vue-i18n
 * hands back for a miss.
 */
const location = computed(() => {
  if (isOnPremise) return t('residency.onPremises');
  if (!region.value) return null;

  const key = `residency.regions.${region.value.labelKey}`;
  return te(key) ? t(key) : region.value.label;
});

/** The sentence behind the caption. Never rendered on the bar itself. */
const detail = computed(() => {
  if (isOnPremise) return t('residency.localDetail');
  return t('residency.cloudDetail', { region: location.value });
});
</script>

<style scoped>
.residency-btn {
  /* Reads as a label that happens to be clickable, not as an action competing
     with the account menu beside it. */
  text-transform: none;
  letter-spacing: normal;
  font-weight: 400;
}

.residency-icon {
  /* `block` because an inline SVG otherwise reserves descender space and rides
     a pixel high against the caption beside it. */
  display: block;
  opacity: 0.7;
}

.residency-label {
  font-size: 0.8125rem;
  margin-left: 8px;
  opacity: 0.85;
}

.residency-detail {
  /* Keeps the last line of the sentence from stranding a single word. */
  text-wrap: pretty;
}
</style>
